// Unified header component for consolidated dashboard
// Carries the dashboard navigation: a top nav bar on desktop, and a menu
// button that opens the grouped sidebar drawer on mobile.

import {
  Box,
  HStack,
  Text,
  Image,
  Flex,
  Container,
  Icon,
  Link as ChakraLink,
} from '@chakra-ui/react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/sdk/auth/hooks';
import { ColorModeSwitcher } from '@/components/ColorModeSwitcher';
import LogoSvg from '../../assets/logo.svg';
import { useDashboardNav, type DashboardNav } from './useDashboardNav';
import { DashboardUserMenu } from './DashboardUserMenu';
import { DashboardMobileNav } from './DashboardMobileNav';

/** Top-level sections, shown inline in the header on desktop. */
function TopNavLinks({ nav }: { nav: DashboardNav }) {
  return (
    <HStack as="nav" aria-label="Main" spacing={1} h="full" align="stretch" data-testid="top-nav">
      {nav.sections.map((section) => {
        const isActive = section.key === nav.activeSectionKey;
        return (
          <ChakraLink
            key={section.key}
            as={RouterLink}
            to={section.links[0].path}
            data-testid={`top-nav-item-${section.key}`}
            aria-current={isActive ? 'page' : undefined}
            display="flex"
            alignItems="center"
            gap={2}
            px={3}
            fontSize="sm"
            fontWeight={isActive ? '600' : '500'}
            color={isActive ? 'text-primary' : 'text-secondary'}
            borderBottom="2px solid"
            borderColor={isActive ? 'brand.500' : 'transparent'}
            mb="-1px"
            whiteSpace="nowrap"
            _hover={{ textDecoration: 'none', color: 'text-primary' }}
          >
            <Icon as={section.icon} boxSize={4} />
            {section.label}
          </ChakraLink>
        );
      })}
    </HStack>
  );
}

/** Sub-tabs for the active section, only when it has more than one page (e.g. Workflows). */
function SectionSubNav({ nav }: { nav: DashboardNav }) {
  const activeSection = nav.sections.find((section) => section.key === nav.activeSectionKey);
  if (!activeSection || activeSection.links.length < 2) return null;

  return (
    <Box borderTop="1px" borderColor="border-default">
      <Container maxW="7xl" py={2}>
        <HStack as="nav" aria-label={activeSection.label} spacing={1} data-testid="top-nav-subnav">
          {activeSection.links.map((link) => {
            const isActive = link.key === nav.activeLinkKey;
            return (
              <ChakraLink
                key={link.key}
                as={RouterLink}
                to={link.path}
                data-testid={`top-nav-subitem-${link.key}`}
                aria-current={isActive ? 'page' : undefined}
                px={3}
                py={1.5}
                borderRadius="md"
                fontSize="sm"
                fontWeight={isActive ? '600' : '500'}
                color={isActive ? 'brand.600' : 'text-secondary'}
                bg={isActive ? 'interactive-brand-hover' : 'transparent'}
                _dark={{ color: isActive ? 'brand.300' : 'text-secondary' }}
                _hover={{ textDecoration: 'none', bg: isActive ? 'interactive-brand-hover' : 'interactive-hover' }}
              >
                {link.label}
              </ChakraLink>
            );
          })}
        </HStack>
      </Container>
    </Box>
  );
}

/**
 * Unified header component for the consolidated dashboard
 * Shows branding, navigation, user menu and color mode switcher
 */
export function UnifiedHeader() {
  const { currentOrg } = useAuth();
  const navigate = useNavigate();
  const nav = useDashboardNav();

  const handleLogoClick = () => {
    if (currentOrg) {
      // Navigate to first available feature
      const firstFeature = currentOrg.feature_access?.[0] || 'reports';
      navigate(`/dashboard/${currentOrg.id}/${firstFeature}`);
    }
  };

  return (
    <Box
      as="header"
      position="sticky"
      top={0}
      zIndex={10}
      bg="bg-canvas"
      backdropFilter="blur(10px)"
      borderBottom="1px"
      borderColor="border-default"
    >
      <Container maxW="7xl">
        <Flex justify="space-between" align="center" h="64px" gap={4}>
          <HStack spacing={2} h="full" minW={0}>
            {nav && (
              <Box display={{ base: 'block', lg: 'none' }}>
                <DashboardMobileNav nav={nav} />
              </Box>
            )}
            <HStack spacing={2} cursor="pointer" onClick={handleLogoClick} flexShrink={0}>
              <Image src={LogoSvg} alt="TaskEye Logo" boxSize="32px" />
              <Text fontSize="xl" fontWeight="bold" color="brand.500">
                TaskEye
              </Text>
            </HStack>
            {currentOrg && (
              <HStack spacing={2} display={{ base: 'flex', lg: 'none' }} minW={0}>
                <Text color="gray.400">|</Text>
                <Text fontSize="md" color="gray.500" noOfLines={1}>
                  {currentOrg.name}
                </Text>
              </HStack>
            )}
            {nav && (
              <Box display={{ base: 'none', lg: 'block' }} h="full" pl={4}>
                <TopNavLinks nav={nav} />
              </Box>
            )}
          </HStack>
          <HStack spacing={2} flexShrink={0}>
            <ColorModeSwitcher />
            {nav && (
              <Box display={{ base: 'none', lg: 'block' }}>
                <DashboardUserMenu nav={nav} />
              </Box>
            )}
          </HStack>
        </Flex>
      </Container>
      {nav && (
        <Box display={{ base: 'none', lg: 'block' }}>
          <SectionSubNav nav={nav} />
        </Box>
      )}
    </Box>
  );
}
